// src/constants/ivrVariables.ts
//
// Catalog of variables that can be embedded in IVR message text / AI prompts as
// `{{token}}` and resolved at call time. Designed as a LAYERED, extensible
// context: each variable declares its `source` so new sources (API mapping,
// pre-provisioned, outbound, AI memory) slot in without changing the syntax.
//
// Canonical syntax: {{ token }}  (whitespace tolerant).
// Legacy alias still honored by the runtime: <value> = the current DTMF choice.
//
// See docs/IVR_VARIABLES_AND_DYNAMIC_DATA.md for the runtime resolution contract
// and the roadmap for the not-yet-live sources.

export type VariableSource =
  | 'call_context'    // from the AGI env / dialplan Redis metadata — live now
  | 'api_mapping'     // mapped from an api_call response (user.* ) — roadmap
  | 'provisioned'     // per-number/caller custom fields in cpass — roadmap
  | 'outbound'        // values supplied when an outbound call is placed — roadmap
  | 'memory';         // AI memory / context layer keyed on call history — roadmap

export interface VariableDef {
  token: string;        // the bare token, e.g. 'caller_id' -> inserted as {{caller_id}}
  label: string;        // human label for the picker
  description: string;
  source: VariableSource;
  availableNow: boolean; // resolvable by the runtime today
}

export const VARIABLE_SOURCE_LABELS: Record<VariableSource, string> = {
  call_context: 'Call context',
  api_mapping: 'API response',
  provisioned: 'Provisioned data',
  outbound: 'Outbound call data',
  memory: 'AI memory (roadmap)',
};

// Call/system context — resolvable today from agi_env + the dialplan's Redis
// session metadata (no external fetch).
export const CALL_CONTEXT_VARIABLES: VariableDef[] = [
  { token: 'caller_id', label: "Caller's number", description: 'The caller MSISDN (agi_callerid).', source: 'call_context', availableNow: true },
  { token: 'dialed_number', label: 'Dialed number', description: 'The DID the caller dialed (agi_dnid).', source: 'call_context', availableNow: true },
  { token: 'session_id', label: 'Call session id', description: 'Unique call id (agi_uniqueid).', source: 'call_context', availableNow: true },
  { token: 'business', label: 'Business / operator', description: 'Business name for this service.', source: 'call_context', availableNow: true },
  { token: 'language', label: 'Language', description: 'Active call language code.', source: 'call_context', availableNow: true },
  { token: 'choice', label: 'Last menu choice', description: 'The value of the last DTMF option chosen (legacy <value>).', source: 'call_context', availableNow: true },
  { token: 'date', label: 'Date', description: 'Current date at call time.', source: 'call_context', availableNow: true },
  { token: 'time', label: 'Time', description: 'Current time at call time.', source: 'call_context', availableNow: true },
];

// Custom per-user data. Tokens are `user.<field>` and become available once a
// source populates them (api_mapping / provisioned / outbound / memory). The
// builder shows a couple of examples; real fields come from the api_call
// response mapping or provisioned data the operator configures.
export const CUSTOM_DATA_EXAMPLES: VariableDef[] = [
  { token: 'user.name', label: 'User name', description: 'Requires an API mapping, provisioned data, or outbound payload that supplies "name".', source: 'api_mapping', availableNow: false },
  { token: 'user.balance', label: 'User balance', description: 'Example: map an api_call response field to user.balance.', source: 'api_mapping', availableNow: false },
];

export const ALL_VARIABLES: VariableDef[] = [...CALL_CONTEXT_VARIABLES, ...CUSTOM_DATA_EXAMPLES];

/** Wrap a token in the canonical placeholder syntax. */
export const asPlaceholder = (token: string) => `{{${token}}}`;

/** Tokens referenced by a piece of text (for validation / preview). */
export const extractTokens = (text: string): string[] => {
  const out: string[] = [];
  const re = /\{\{\s*([\w.]+)\s*\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text || ''))) out.push(m[1]);
  return out;
};
